// R.I.S.A default content — greeting, insight chips, the suggested-question
// pool, and the canned response renderers. Ported verbatim from the PCS v3
// standalone prototype's Genie (RESPONSE_RENDERERS / DEFAULT_POOL / DEFAULT_INSIGHTS).
// Each renderer returns the body of a `.genie-resp-card`. A page can override any
// of these by passing a `context` to the Risa provider's open() (see RisaProvider).
import { ArrowRightIcon, DownloadIcon } from '../../icons/Icons';

const Arrow = () => <ArrowRightIcon width="12" height="12" />;

// ── Default suggested-question pool (4 shown at a time, asked ones drop off) ──
export const DEFAULT_POOL = [
  { q: 'What happens if I raise my contribution to 10%?', tag: 'match' },
  { q: 'Why should I update my beneficiary?', tag: 'benef' },
  { q: 'What do I do with my 1099-R?', tag: 'tax' },
  { q: 'How does my portfolio compare to the market?', tag: 'perf' },
  { q: 'When can I retire comfortably?', tag: 'ready' },
  { q: "What's the difference between pre-tax and Roth?", tag: 'contrib' },
  { q: 'How is my asset allocation doing?', tag: 'alloc' },
  { q: "What's my projected income at 60 vs 65?", tag: 'income' },
  { q: 'How much should I save for retirement?', tag: 'save' },
  { q: 'Am I on track?', tag: 'track' },
];

// ── Default insight chips (top of the stream) ──
export const DEFAULT_INSIGHTS = [
  { tone: 'green', text: <>Bump to <strong>10%</strong> and you&apos;ll capture the full <strong>$1,820/yr</strong> match — about <strong>$85/paycheck</strong> after-tax.</> },
  { tone: 'blue', text: <>Your beneficiary info hasn&apos;t been touched since <strong>2019</strong>. A 60-second review keeps things current.</> },
  { tone: 'purple', text: <>Portfolio is tracking your target — <strong>+8.4% YTD</strong>, in line with your glide path.</> },
];

// ── Default greeting node ──
export const DEFAULT_GREETING = (
  <>Hey <span className="name">Maya</span>, I had a look at your dashboard. Here&apos;s what stood out today.</>
);

// ── Canned response cards, keyed by the exact question text ──
export const RESPONSE_RENDERERS = {
  'What happens if I raise my contribution to 10%?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">Quick look at staying at 8% vs bumping to 10%.</p>
      <div className="resp-compare">
        <div className="compare-col">
          <div className="compare-col-h">Current</div>
          <div className="compare-col-rate">8<span>%</span></div>
          <div className="compare-stats">
            <div><span className="l">Take-home</span><span className="v">No change</span></div>
            <div><span className="l">Match captured</span><span className="v">$4,210/yr</span></div>
            <div><span className="l">Balance at 65</span><span className="v">$1.42M</span></div>
          </div>
        </div>
        <div className="compare-col hi">
          <div className="compare-col-h">Boosted</div>
          <div className="compare-col-rate">10<span>%</span></div>
          <div className="compare-stats">
            <div><span className="l">Take-home</span><span className="v">−$85/paycheck</span></div>
            <div><span className="l">Match captured</span><span className="v pos">+$1,820/yr</span></div>
            <div><span className="l">Balance at 65</span><span className="v pos">+$48K</span></div>
          </div>
        </div>
      </div>
      <p className="resp-note">The after-tax cost is small because contributions reduce your taxable income — you only feel about $85 of the $142 bump per paycheck.</p>
      <button className="genie-resp-cta" type="button">Raise to 10% <Arrow /></button>
    </div>
  ),

  'How is my asset allocation doing?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">Your mix lines up with a <strong>Moderate Growth</strong> profile — appropriate for someone 28 years from retirement.</p>
      <div className="resp-alloc-bar">
        <span style={{ width: '56%', background: '#0077CC' }} />
        <span style={{ width: '16%', background: '#552299' }} />
        <span style={{ width: '20%', background: '#119944' }} />
        <span style={{ width: '8%', background: '#FFBB11' }} />
      </div>
      <div className="resp-alloc-legend">
        <span><i style={{ background: '#0077CC' }} />US Equity <b>56%</b></span>
        <span><i style={{ background: '#552299' }} />Intl <b>16%</b></span>
        <span><i style={{ background: '#119944' }} />Bonds <b>20%</b></span>
        <span><i style={{ background: '#FFBB11' }} />Cash <b>8%</b></span>
      </div>
      <div className="resp-status ok">
        <span className="resp-status-dot" />
        On target · Slightly equity-heavy, which suits a long time horizon
      </div>
      <button className="genie-resp-cta" type="button">Review allocation <Arrow /></button>
    </div>
  ),

  'When can I retire comfortably?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">Three scenarios based on your current pace.</p>
      <div className="resp-projections">
        {[
          { age: 60, mo: '$4,210', pct: 65, status: 'Behind goal', tone: 'behind' },
          { age: 65, mo: '$5,920', pct: 91, status: 'On track', tone: 'ok' },
          { age: 67, mo: '$6,780', pct: 104, status: 'Ahead', tone: 'ahead' },
        ].map((p) => (
          <div key={p.age} className={'proj-tile ' + p.tone}>
            <div className="proj-age">Age {p.age}</div>
            <div className="proj-mo">{p.mo}<span>/mo</span></div>
            <div className="proj-pct">{p.pct}% of goal</div>
            <div className={'proj-status ' + p.tone}><span className="proj-status-dot" />{p.status}</div>
          </div>
        ))}
      </div>
      <p className="resp-note">Each year between 60 and 67 adds roughly <strong>$350/mo</strong> to your retirement paycheck.</p>
      <button className="genie-resp-cta" type="button">Plan around age 65 <Arrow /></button>
    </div>
  ),

  "What's my projected income at 60 vs 65?": () => (
    <div className="genie-resp-card">
      <p className="resp-intro">Five extra years of contributions + compounding makes a real difference.</p>
      <div className="resp-compare">
        <div className="compare-col">
          <div className="compare-col-h">Age 60</div>
          <div className="compare-col-big">$4,210<span>/mo</span></div>
          <div className="resp-mini-bar"><span style={{ width: '65%', background: '#E08A1A' }} /></div>
          <div className="compare-foot">65% of $6,500 goal</div>
        </div>
        <div className="compare-col hi">
          <div className="compare-col-h">Age 65</div>
          <div className="compare-col-big">$5,920<span>/mo</span></div>
          <div className="resp-mini-bar"><span style={{ width: '91%', background: '#0077CC' }} /></div>
          <div className="compare-foot">91% of $6,500 goal</div>
        </div>
      </div>
      <p className="resp-note">Waiting from 60 to 65 adds roughly <strong>+$1,710/mo</strong> — because you&apos;re contributing five more years and your balance keeps compounding.</p>
      <button className="genie-resp-cta" type="button">Explore strategy <Arrow /></button>
    </div>
  ),

  'Why should I update my beneficiary?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">Your beneficiary form was last updated in <strong>2019</strong> — almost 7 years ago.</p>
      <ul className="resp-bullets">
        <li>A beneficiary is who inherits this account if something happens to you.</li>
        <li>Outdated info can override your will — even an ex-spouse stays a beneficiary until you change it.</li>
        <li>Without a valid beneficiary on file, the account goes through probate — slow and costly.</li>
      </ul>
      <div className="resp-callout warn">
        <span className="l">Last reviewed</span>
        <span className="v">Aug 14, 2019</span>
      </div>
      <button className="genie-resp-cta" type="button">Update beneficiary <Arrow /></button>
    </div>
  ),

  'What do I do with my 1099-R?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">A 1099-R reports money that came out of your retirement plan during the year — you&apos;ll need it to file your 2025 taxes.</p>
      <div className="resp-doc-row">
        <div className="doc-ic"><DownloadIcon width="16" height="16" /></div>
        <div className="doc-text">
          <div className="doc-h">Form 1099-R · Tax year 2025</div>
          <div className="doc-sub">PDF · 142 KB · available now</div>
        </div>
        <span className="doc-badge">New</span>
      </div>
      <ul className="resp-bullets">
        <li>Hand it to your tax preparer, or upload it to TurboTax / H&amp;R Block.</li>
        <li>You owe tax only on pre-tax withdrawals — Roth distributions are reported but tax-free.</li>
      </ul>
      <button className="genie-resp-cta" type="button">Download 1099-R <Arrow /></button>
    </div>
  ),

  'How does my portfolio compare to the market?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">You&apos;re tracking right alongside the broader market this year.</p>
      <div className="resp-perf">
        <div className="perf-row">
          <span className="perf-lbl">Your portfolio</span>
          <div className="perf-bar"><span style={{ width: '91%', background: '#0077CC' }} /></div>
          <span className="perf-val pos">+8.4%</span>
        </div>
        <div className="perf-row">
          <span className="perf-lbl">Blended benchmark</span>
          <div className="perf-bar"><span style={{ width: '100%', background: 'var(--text-2)' }} /></div>
          <span className="perf-val">+9.2%</span>
        </div>
      </div>
      {/* No allocation explanation here: the row above now reads "Blended benchmark", which
          this PR defines as an index built from the participant's OWN asset-class weights —
          so their bond/international tilt sits on both sides of the gap and cannot be its
          cause. Same reason the clause was dropped from CompareSection's trailing insight. */}
      <p className="resp-note">You&apos;re slightly behind your blended benchmark — the gap is fund costs and tracking, not your mix.</p>
      <button className="genie-resp-cta" type="button">View portfolio details <Arrow /></button>
    </div>
  ),

  "What's the difference between pre-tax and Roth?": () => (
    <div className="genie-resp-card">
      <p className="resp-intro">Same account, two ways to handle taxes.</p>
      <div className="resp-compare">
        <div className="compare-col">
          <div className="compare-col-h">Pre-tax</div>
          <ul className="compare-points">
            <li className="pos">Tax break <em>now</em></li>
            <li className="pos">Lower withholding each paycheck</li>
            <li>Taxed when you withdraw</li>
          </ul>
        </div>
        <div className="compare-col">
          <div className="compare-col-h">Roth</div>
          <ul className="compare-points">
            <li className="pos">Tax-free in retirement</li>
            <li className="pos">Best if you&apos;ll be in a higher bracket later</li>
            <li>No break today</li>
          </ul>
        </div>
      </div>
      <div className="resp-callout">
        <span className="l">Risa&apos;s read for you</span>
        <span className="v">Mix of both · ~30% Roth at age 37</span>
      </div>
      <p className="resp-note">At 37 you&apos;ve got 28 years for tax-free Roth growth to compound — a meaningful slice of Roth often pays off.</p>
      <button className="genie-resp-cta" type="button">Adjust contributions <Arrow /></button>
    </div>
  ),

  'How much should I save for retirement?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro">A common rule of thumb is <strong>15% of pay</strong> per year — counting you and your employer combined.</p>
      <div className="resp-savings">
        <div className="savings-track">
          <span className="savings-fill" style={{ width: '80%' }} />
          <span className="savings-marker" style={{ left: '80%' }}>
            <span className="savings-marker-lbl">12% today</span>
          </span>
          <span className="savings-goal" style={{ left: '100%' }}>
            <span className="savings-goal-lbl">15% goal</span>
          </span>
        </div>
      </div>
      <div className="resp-mini-stats four">
        <div><span className="l">You</span><span className="v">8%</span></div>
        <div><span className="l">Acme match</span><span className="v">4%</span></div>
        <div><span className="l">Total</span><span className="v">12%</span></div>
        <div><span className="l">Goal</span><span className="v">15%</span></div>
      </div>
      <p className="resp-note">Raising your rate to <strong>11%</strong> lands you at the 15% target with match included.</p>
      <button className="genie-resp-cta" type="button">Raise to 11% <Arrow /></button>
    </div>
  ),

  'Am I on track?': () => (
    <div className="genie-resp-card">
      <p className="resp-intro"><strong>Yes — with room to improve.</strong> Here&apos;s the quick read.</p>
      <div className="resp-score-row">
        <div className="score-ring">
          <svg viewBox="0 0 60 60" width="64" height="64">
            <circle cx="30" cy="30" r="26" fill="none" stroke="var(--bg-sunken)" strokeWidth="6" />
            <circle
              cx="30" cy="30" r="26" fill="none" stroke="#0077CC" strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${163.36 * 0.82} ${163.36}`}
              transform="rotate(-90 30 30)"
            />
          </svg>
          <div className="score-ring-text">82</div>
        </div>
        <div className="score-text">
          <div className="score-h">Readiness score</div>
          <div className="score-sub">Out of 100 · <span className="pos">+12 YTD</span></div>
        </div>
      </div>
      <div className="resp-mini-stats two">
        <div><span className="l">Projected at 65</span><span className="v">$5,920/mo</span></div>
        <div><span className="l">From goal</span><span className="v">−$580/mo</span></div>
      </div>
      <div className="resp-section-h">Three next steps</div>
      <ul className="resp-bullets">
        <li>Raise contribution to 10% to close most of the gap</li>
        <li>Confirm your beneficiary info is current</li>
        <li>Consider routing a portion of contributions to Roth</li>
      </ul>
      <button className="genie-resp-cta" type="button">See full strategy <Arrow /></button>
    </div>
  ),
};
